/**
 * A ficha do "+ Empresa" num modal sobre a tela de quem a pediu (13.1; movida e parametrizada na
 * 13.3).
 *
 * Ela nasceu dentro de `FormularioDeNegocio.tsx`, para o "Criar nova empresa" do Novo negócio. A
 * 13.3 a trouxe para cá porque o painel "Dados do contato" do WhatsApp abre **a mesma** ficha no
 * "Cadastrar Empresa com este contato" — uma segunda cópia divergiria no primeiro ajuste de trava de
 * foco, que é justamente o que não pode divergir. O que muda entre os dois hospedeiros é só o texto
 * do texto que a ficha recebe (o digitado no campo, o nome do contato) e o Responsável inicial; o que
 * acontece com a Empresa criada é do hospedeiro (no Negócio ela fica escolhida, no painel o contato é
 * vinculado a ela).
 *
 * - Trava de foco **própria** (`Tab`/`Shift+Tab` dão a volta aqui dentro, e o evento não sobe para a
 *   trava da janela de baixo).
 * - `Esc` é tratado no próprio `onKeyDown` — o React despacha na raiz, antes do ouvinte que a janela
 *   pôs no `document` — e marcado (`preventDefault`): a janela de baixo não fecha. Ele confere
 *   `defaultPrevented` porque o `Dialogo` de descarte ouve em captura e marca primeiro.
 * - Com algo preenchido, `Esc` e o fundo **perguntam** antes de descartar; "Cancelar" sai direto.
 * - **Desde a 13.14a a Empresa nasce com as Pessoas de uma vez** (DW-379): não há mais a Empresa
 *   criada com Pessoas pendentes, nem o "Seguir sem as pessoas pendentes?" da 13.2.
 * - **Enquanto a ficha envia** (`aoMudarEnvio`), `Esc` e o fundo não fazem nada: fechar no meio do
 *   envio deixaria a Empresa criada sem ninguém recebê-la. E a Empresa é entregue **uma** vez.
 * - **O texto inicial** (13.14a, DW-420) vai ao **Nome fantasia** — quase sempre é a marca que a
 *   pessoa procurou e não achou; a razão social fica para o CNPJ. O texto sem letra com 11 ou 14
 *   dígitos (um CNPJ/CPF colado) vai ao CNPJ/CPF.
 * - O ERP que acha a ficha do documento digitado (`aoEncontrar`) devolve o `id` dela, e o que fazer
 *   com ele é do hospedeiro.
 */

import { type KeyboardEvent as TeclaNoModal, useRef, useState } from 'react'

import type { EmpresaEscolhida } from '@/modulos/cadastro/CampoDeEmpresa'
import {
  type DesfechoDaFicha,
  FormularioDeEmpresa,
  type ValoresIniciais,
} from '@/modulos/cadastro/FormularioDeEmpresa'
import { nomeExibidoDaEmpresa } from '@/modulos/cadastro/nome-da-empresa'
import { formatarNumeroDeWhatsapp } from '@/modulos/comunicacao/contexto-da-conversa'
import { Dialogo } from '@/modulos/negocios/Dialogo'
import {
  DDI_DO_BRASIL,
  INDICE_DO_NONO,
  TAMANHO_BRASILEIRO_COM_O_NONO,
  TAMANHO_BRASILEIRO_SEM_O_NONO,
} from '@/telefone/chave-do-telefone'
import { formatarTelefone } from '@/telefone/mascara-do-telefone'
import './modal-da-nova-empresa.css'

/** O dígito verificador do CPF e do CNPJ (módulo 11), pelos pesos de cada posição. */
function digitoVerificador(digitos: string, pesos: readonly number[]): number {
  const soma = pesos.reduce((total, peso, posicao) => total + peso * Number(digitos[posicao]), 0)
  const resto = soma % 11
  return resto < 2 ? 0 : 11 - resto
}

/** Os 11 ou 14 dígitos fecham os verificadores do CPF ou do CNPJ? (Os repetidos não fecham.) */
function fechaOsVerificadores(digitos: string): boolean {
  if (/^(\d)\1*$/.test(digitos)) {
    return false
  }
  const pesosDoCpf = [11, 10, 9, 8, 7, 6, 5, 4, 3, 2]
  const pesosDoCnpj = [6, 5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2]
  const pesos = digitos.length === 11 ? pesosDoCpf : digitos.length === 14 ? pesosDoCnpj : null
  if (pesos === null) {
    return false
  }
  const primeiro = digitoVerificador(digitos, pesos.slice(1))
  const segundo = digitoVerificador(digitos, pesos)
  return (
    Number(digitos[digitos.length - 2]) === primeiro &&
    Number(digitos[digitos.length - 1]) === segundo
  )
}

/**
 * Os valores iniciais da ficha a partir do texto do hospedeiro (13.14a, DW-420): o nome fantasia, ou
 * o CNPJ/CPF quando o texto é só o documento (sem letra, 11 ou 14 dígitos que fecham os
 * verificadores — o celular com DDD também tem 11 e vai ao Nome fantasia).
 */
export function valoresDoTexto(texto: string, telefone: string | undefined): ValoresIniciais {
  const aparado = texto.trim()
  const digitos = aparado.replace(/\D/g, '')
  const ehDocumento = aparado !== '' && !/\p{L}/u.test(aparado) && fechaOsVerificadores(digitos)
  return {
    ...(ehDocumento ? { documento: digitos } : aparado === '' ? {} : { nome_fantasia: aparado }),
    ...(telefone === undefined ? {} : { telefone }),
  }
}

/** O que o modal pergunta antes de descartar uma Empresa já preenchida. */
export const PERGUNTA_DA_NOVA_EMPRESA = 'O que foi preenchido na empresa nova se perde.'

/**
 * Os elementos que recebem foco por `Tab` dentro do modal — o que a trava de foco percorre.
 *
 * `:not(:disabled)`, e não `:not([disabled])` (13.2): um campo dentro de um `fieldset disabled` fica
 * desabilitado **sem** o atributo — pelo atributo, a trava mandaria o foco para um campo que não o
 * aceita.
 */
export const FOCAVEIS =
  'a[href], button:not(:disabled), input:not(:disabled), select:not(:disabled), ' +
  'textarea:not(:disabled), [tabindex]:not([tabindex="-1"])'

/**
 * O telefone do contato da conversa como a ficha nova o recebe (D-15 da 13.2): o número brasileiro
 * que o provedor grava com DDI (`55` + 10 dígitos, ou `55` + 11 com o nono) vai na grafia nacional
 * da máscara, `(22) 99755-0997` — a mesma que a ficha grava quando se digita. O resto continua na
 * grafia do WhatsApp (`formatarNumeroDeWhatsapp`), que o campo trata como livre.
 */
export function telefoneDaConversaNaFicha(numero: string): string {
  const digitos = numero.replace(/\D/g, '')
  const soDigitos = digitos !== '' && digitos === numero.trim().replace(/^\+/, '')
  if (soDigitos && digitos.startsWith(DDI_DO_BRASIL)) {
    const nacional = digitos.slice(DDI_DO_BRASIL.length)
    const comFormato =
      nacional.length === TAMANHO_BRASILEIRO_SEM_O_NONO ||
      (nacional.length === TAMANHO_BRASILEIRO_COM_O_NONO && nacional[INDICE_DO_NONO] === '9')
    const grafia = formatarTelefone(nacional)
    if (comFormato && grafia !== nacional) {
      return grafia
    }
  }
  return formatarNumeroDeWhatsapp(numero)
}

/**
 * Há algo preenchido na ficha? (os somente-leitura não contam) — e, desde a 13.19c1, a linha com a
 * Pessoa já cadastrada escolhida (`data-escolhida`), que não tem campo de texto.
 */
export function temAlgoPreenchido(caixa: HTMLElement | null): boolean {
  if (caixa === null) {
    return false
  }
  if (caixa.querySelector('[data-escolhida="sim"]') !== null) {
    return true
  }
  return Array.from(
    caixa.querySelectorAll<HTMLInputElement | HTMLTextAreaElement>(
      'input:not([type="hidden"]):not([type="checkbox"]):not([type="radio"]):not([readonly]), textarea',
    ),
  ).some((campo) => campo.value.trim() !== '')
}

export interface PropriedadesDoModalDaNovaEmpresa {
  /** O texto com que a ficha começa (`valoresDoTexto`): o digitado no campo, ou o nome do contato. */
  textoInicial: string
  /** O telefone do contato da conversa (o Novo negócio e o painel do WhatsApp); ausente, nenhum. */
  telefone?: string
  /**
   * O Responsável com que a ficha começa, editável (o do Negócio, no Novo negócio). Ausente, a
   * ficha mostra quem cadastra, somente leitura — é o caso do painel do contato.
   */
  responsavelInicial?: string
  aoCancelar: () => void
  /**
   * A Empresa criada (com as Pessoas, de uma vez). O `desfecho` diz se alguma linha de Pessoa
   * **adotou** a Pessoa solta do WhatsApp (13.4).
   */
  aoEscolher: (empresa: EmpresaEscolhida, desfecho: DesfechoDaFicha) => void
  /** O ERP achou a ficha daquele documento: o `id` dela, para o hospedeiro decidir. */
  aoEncontrar: (empresaId: string) => void
  /**
   * Sem o cartão Pessoas da ficha nova (13.15): o "+ Pessoa" abre o modal para criar a Empresa da
   * Pessoa que **ele** cadastra — uma segunda lista de Pessoas ali seria outra Pessoa.
   */
  semCartaoDePessoas?: boolean
}

export function ModalDaNovaEmpresa({
  textoInicial,
  telefone,
  responsavelInicial,
  aoCancelar,
  aoEscolher,
  aoEncontrar,
  semCartaoDePessoas = false,
}: PropriedadesDoModalDaNovaEmpresa) {
  const caixa = useRef<HTMLDivElement>(null)
  const comecouNoFundo = useRef(false)
  const [confirmando, setConfirmando] = useState(false)
  /* O envio da ficha (a Empresa e as Pessoas) está a caminho: Esc e o fundo não fazem nada. */
  const enviando = useRef(false)
  /* A Empresa vai ao hospedeiro **uma** vez. */
  const jaEscolheu = useRef(false)
  /* Alguma linha de Pessoa adotou a Pessoa solta (13.4). */
  const adotouContato = useRef(false)
  /* Os valores iniciais, calculados uma vez: a ficha os lê só ao montar. */
  const [valoresIniciais] = useState(() => valoresDoTexto(textoInicial, telefone))
  const escolherUmaVez = (empresa: EmpresaEscolhida) => {
    if (jaEscolheu.current) {
      return
    }
    jaEscolheu.current = true
    aoEscolher(empresa, { contatoAdotado: adotouContato.current })
  }

  const pedirParaFechar = () => {
    if (enviando.current) {
      return
    }
    if (temAlgoPreenchido(caixa.current)) {
      setConfirmando(true)
      return
    }
    aoCancelar()
  }

  const aoTeclar = (evento: TeclaNoModal<HTMLDivElement>) => {
    if (evento.key === 'Escape') {
      if (evento.defaultPrevented || confirmando) {
        return
      }
      evento.preventDefault()
      pedirParaFechar()
      return
    }
    if (evento.key !== 'Tab' || caixa.current === null) {
      return
    }
    // A trava é desta caixa: o `Tab` não sobe para a da janela de baixo.
    evento.stopPropagation()
    /*
     * Em ordem de documento, conferida: o `querySelectorAll` já devolve assim no navegador, mas o
     * do jsdom agrupa por seletor quando a lista tem `:disabled` — e o "último" deixaria de ser o
     * último.
     */
    const focaveis = Array.from(caixa.current.querySelectorAll<HTMLElement>(FOCAVEIS)).sort(
      (um, outro) => (um.compareDocumentPosition(outro) & Node.DOCUMENT_POSITION_FOLLOWING ? -1 : 1),
    )
    const primeiro = focaveis[0]
    const ultimo = focaveis[focaveis.length - 1]
    if (primeiro === undefined || ultimo === undefined) {
      evento.preventDefault()
      return
    }
    const ativo = document.activeElement
    if (evento.shiftKey && (ativo === primeiro || ativo === caixa.current)) {
      evento.preventDefault()
      ultimo.focus()
    } else if (!evento.shiftKey && ativo === ultimo) {
      evento.preventDefault()
      primeiro.focus()
    }
  }

  return (
    <div
      className="nova-empresa__fundo"
      role="presentation"
      onMouseDown={(evento) => {
        comecouNoFundo.current = evento.target === evento.currentTarget
      }}
      onClick={(evento) => {
        const doFundo = comecouNoFundo.current && evento.target === evento.currentTarget
        comecouNoFundo.current = false
        if (doFundo) {
          pedirParaFechar()
        }
      }}
    >
      <div
        ref={caixa}
        className="nova-empresa__modal"
        role="dialog"
        aria-modal="true"
        aria-label="Nova empresa"
        tabIndex={-1}
        onKeyDown={aoTeclar}
      >
        <FormularioDeEmpresa
          valoresIniciais={valoresIniciais}
          {...(responsavelInicial === undefined ? {} : { responsavelInicial })}
          aoCancelar={aoCancelar}
          aoMudarEnvio={(valor) => {
            enviando.current = valor
          }}
          aoAdotarContato={() => {
            adotouContato.current = true
          }}
          aoSalvar={(empresa) => {
            escolherUmaVez({ id: empresa.id, nome: nomeExibidoDaEmpresa(empresa) })
          }}
          aoEncontrarFicha={aoEncontrar}
          semCartaoDePessoas={semCartaoDePessoas}
        />
      </div>
      {confirmando ? (
        <Dialogo
          titulo="Descartar a empresa nova?"
          subtitulo={PERGUNTA_DA_NOVA_EMPRESA}
          rotuloDoConfirmar="Descartar"
          ocupado={false}
          erro={undefined}
          aoFechar={() => {
            setConfirmando(false)
            caixa.current?.focus()
          }}
          aoConfirmar={aoCancelar}
        />
      ) : null}
    </div>
  )
}
